/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
import { task } from 'ember-concurrency';
import { hash } from 'ember-concurrency';

import AnalyticsService from 'cio/services/analytics';
import DeliveryModel from 'cio/models/delivery';
import CampaignModel from 'cio/models/campaign';
import NewsletterModel from 'cio/models/newsletter';
import TransactionalMessageModel from 'cio/models/transactional-message';
import DeliveryDetailModel from 'cio/models/delivery-detail';
import MessageSettingsService from 'cio/services/message-settings';
import isExpiredDeliveryInEventTriggeredCampaign from 'cio/utils/campaigns/is-expired-delivery-in-event-triggered-campaign';

import { RetryEvent } from 'cio/components/cio-deliveries/list';

interface ExpansionArgs {
  delivery: DeliveryModel;
  detail: DeliveryDetailModel | null;
  isMessagingLimitEnabled: boolean;
  onSend: () => unknown;
  onDelete: () => unknown;
  onRetry: (options?: { overrideLimit: boolean }) => unknown;
  onRedeliver: (options: { tracked: boolean; overrideLimit?: boolean }) => unknown;
  hasReadOnlyPermission: boolean;
}

export default class Expansion extends Component<ExpansionArgs> {
  @service analytics!: AnalyticsService;
  @service messageSettings!: MessageSettingsService;

  @tracked showRetryModal = false;
  @tracked redeliveryDisabled = false;

  // first column of timestamps
  @computed('args.delivery.actionInfo.deliveryStates')
  get deliveryTimestamps() {
    return (this.args.delivery.actionInfo?.deliveryStates ?? []).filter((s) => s !== 'holdout');
  }

  // second column of timestamps
  @readOnly('args.delivery.actionInfo.deliveryActions')
  postDeliveryTimestamps!: Record<string, number>[];

  @tracked campaign: CampaignModel | null = null;
  @tracked newsletter: NewsletterModel | null = null;
  @tracked transactionalMessage: TransactionalMessageModel | null = null;
  @tracked deliveryDetail: DeliveryDetailModel | null = null;

  _trackRetryEvent(eventKey: RetryEvent, additionalParams: { overrideLimit?: boolean } = {}) {
    let params = {
      count: 1,
      statuses: [this.args.delivery.displayState],
      ...additionalParams,
    };

    this.analytics.track('message', eventKey, params);
  }

  get redeliverButtonDisabled() {
    return (
      (this.args.detail?.hideBody ?? false) ||
      this.redeliveryDisabled ||
      isExpiredDeliveryInEventTriggeredCampaign(this.args.delivery, this.campaign)
    );
  }

  get showErrorReason() {
    let { delivery } = this.args;

    return (
      ['failed', 'bounced', 'suppressed', 'attempted', 'undeliverable'].includes(delivery.displayState) &&
      delivery.meta?.reason
    );
  }

  get showSendAndDeleteButtons() {
    let { delivery } = this.args;
    return delivery.isDrafted || delivery.isFailed || delivery.isUndeliverable || delivery.isRedeliverableAttempted;
  }

  maybeFetchRelationships = task({ drop: true }, async () => {
    let { delivery } = this.args;

    let parents = await hash({
      campaign: delivery.belongsTo('campaign').load(),
      newsletter: delivery.belongsTo('newsletter').load(),
      transactionalMessage: delivery.belongsTo('transactionalMessage').load(),
      deliveryDetail: delivery.belongsTo('detail').load(),
    });

    this.campaign = parents.campaign;
    this.newsletter = parents.newsletter;
    this.transactionalMessage = parents.transactionalMessage;
    this.deliveryDetail = parents.deliveryDetail;
  });

  @action
  onRedeliveryConfirmed(options: { tracked: boolean; overrideLimit?: boolean }) {
    this.redeliveryDisabled = true;
    return this.args.onRedeliver(options);
  }

  @action
  retry() {
    if (
      (this.args.delivery.isUndeliverable || this.args.delivery.isRedeliverableAttempted) &&
      this.messageSettings.isMessagingLimitEnabled
    ) {
      this.showRetryModal = true;
      this._trackRetryEvent('retry_send_started');
    } else {
      this.args.onRetry();
      this._trackRetryEvent('retry_send_confirmed', { overrideLimit: false });
    }
  }

  @action
  cancelRetry() {
    this.showRetryModal = false;
    this._trackRetryEvent('retry_send_canceled');
  }

  @action
  confirmRetry(overrideLimit: boolean) {
    this.showRetryModal = false;
    this.args.onRetry({ overrideLimit });
    this._trackRetryEvent('retry_send_confirmed', { overrideLimit });
  }
}
